import React, { useState, forwardRef, useEffect } from 'react';

import './ImageRotateViewer.scss';

const ImageRotateViewer = forwardRef(({ className, file, rotate, src }, ref) => {
  const [imageSrc, setImageSrc] = useState('');

  const applyFilter = () => {
    // Only run the canvas pipeline when a file is provided (i.e. rotation is active).
    if (!file) return;

    const canvas = ref.current;
    const context = canvas?.getContext('2d');
    const fileReader = new FileReader();

    fileReader.onload = () => {
      const image = new Image();

      image.onload = () => {
        if (canvas && context) {
          canvas.width = image.width;
          canvas.height = image.height;
          context.save();
          if (rotate) {
            const centerX = canvas.width / 2;
            const centerY = canvas.height / 2;
            context.translate(centerX, centerY);
            context.rotate((rotate * Math.PI) / 180);
            context.translate(-centerX, -centerY);
          }
          context.drawImage(image, 0, 0, canvas.width, canvas.height);

          context.restore();
        }
      };

      image.src = fileReader.result;
      if (window.location.protocol.includes('http:')) image.crossOrigin = 'anonymous';
    };
    fileReader.readAsDataURL(file);
  };

  // Single effect: when file is provided create a blob URL (for the rotate/canvas pipeline);
  // when file is absent or removed, fall back to the src prop (S3 URL or thumbnail blob).
  // This prevents FileReader from running for the thousands of completed, non-rotating tiles.
  useEffect(() => {
    if (!file) {
      setImageSrc(src);
      return;
    }
    const fileSrc = URL.createObjectURL(file);
    setImageSrc(fileSrc);
    return () => URL.revokeObjectURL(fileSrc);
  }, [file, src]);

  useEffect(() => {
    applyFilter();
  }, [file, imageSrc, rotate]);

  if (rotate) {
    return <canvas id="canvas" ref={ref} />;
  }

  // loading="lazy" prevents the browser from fetching all S3 images at once when a large
  // list of completed tiles renders simultaneously (e.g. after clearing the error filter).
  return <img className={className} src={imageSrc} alt="No Thumbnail" loading="lazy" />;
});

export default ImageRotateViewer;
